<template>
  <div class="map">
    <div class="map-layout">
      <div class="map-panel">
        <r-main-panel
          :title="$t('map')"
          icon="vizualization"
          v-if="mapMode === 'vizualization'"
        >
          <map-layers
            :layer-controller="LayerController"
            :track-controller="TrackController"
            :map="mapgl"
            @toggle-module="toggleModule"
          />
        </r-main-panel>
        <r-main-panel
          title="Редактор геометрии"
          icon="vizualization"
          v-else-if="mapMode === 'editor'"
        >
          <map-editor
            :map="mapgl"
            :editor-state="editorState"
            :layer-controller="LayerController"
            :editor-controller="EditorController"
          />
        </r-main-panel>
        <div v-if="isSecondPanelActive" class="map-panel-second">
          <layer-settings
            v-if="secondPanelMode === 'layer-settings'"
            :id="layerSettingsId"
            :style-config="styleConfig"
            :style-controller="StyleController"
            :layer-controller="LayerController"
            :diagram-controller="DiagramController"
            :heatmap-controller="HeatmapController"
            :matrices-controller="MatricesController"
            @close-settings="toggleModule"
          />
        </div>
      </div>
      <div class="map-container" id="map-container">
        <div class="map-tools-container">
          <div class="map-actions">
            <map-tools
              :tools="['baselayers', '3d', 'reset', 'screenshot']"
              :map="mapgl"
              @change-prop="changeProp"
              @toggle-base-layer="toggleBaselayer"
              :map-bearing="mapBearing"
              :is3d="is3d"
              :baselayer-id="baselayerId"
            />
            <map-legend
              v-if="!demoGuest"
              :map="mapgl"
              :map-mode="mapMode"
              :objects-counts="objectsCounts"
              :features-in-bbox-counts="featuresInBboxCounts"
              :is-update="isUpdate"
            />
          </div>
          <map-popup :settings="popupSettings" />
          <map-popup-list :settings="popupListSettings" />
          <div v-if="mapLoading" class="map-loader">
            <i class="el-icon-loading" />
            Загрузка данных...
          </div>
          <timemachine
            v-if="timemachineState.enabled"
            @close="closeTimemachine"
            @refresh="timemachineChangeHandler"
            @change-field="changeTimemachineField"
            :timemachine-filter="timemachineFilter"
            :time-machine-state="timemachineState"
            :baselayer-id="baselayerId"
          />
        </div>
      </div>
      <r-info-panel v-if="activeCards.length">
        <active-cards module="map" />
      </r-info-panel>
    </div>

    <delete-modal
      v-if="deleteModal"
      :visible="deleteModal"
      :editor-state="editorState"
      :draw="draw"
      @toggle-delete-modal="toggleDeleteModal"
    />
    <create-modal
      v-if="createModal"
      :visible="createModal"
      :editor-state="editorState"
      :editor-controller="EditorController"
      :draw="draw"
      @toggle-create-modal="toggleCreateModal"
    />
  </div>
</template>

<script>
import cloneDeep from "lodash.clonedeep";
import extent from "turf-extent";

import ActiveCards from "@/components/object-info/active-cards";
import MapTools from "./components/map-tools/map-tools";
import MapPopup from "./components/map-popup/map-popup";
import MapPopupList from "./components/map-popup-list/map-popup-list";
import MapLegend from "./components/map-legend/map-legend";
import MapLayers from "./components/map-layers/map-layers";
import MapEditor from "./components/map-editor/map-editor";
import LayerSettings from "./components/layer-settings/settings";
import Timemachine from "./components/timemachine/timemachine.vue";
import DeleteModal from "./components/map-editor/delete-modal";
import CreateModal from "./components/map-editor/create-modal";

import constants from "@/constants/url";
import { initMap, initControllers } from "./core";
import { initialEditorState, initialTimemachineState } from "./configs";
import { toggleActiveObject } from "./controllers/active-object-controller";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import directionIconPath from "@/assets/images/map/direction-arrow.png";

export default {
  components: {
    ActiveCards,
    MapTools,
    MapPopup,
    MapPopupList,
    MapLegend,
    MapLayers,
    MapEditor,
    LayerSettings,
    Timemachine,
    DeleteModal,
    CreateModal
  },
  data() {
    return {
      windowId: "map",
      mapgl: null,
      baselayers: this.$store.state.map.baselayers,
      datasources: this.$store.state.datasources,
      baselayerId: this.$store.state.user_config.baselayerId || 5,
      requestedFields: this.$store.state.map.requestedFields || {},
      objectFieldsConfigs: this.$store.state.map.objectFieldsConfigs || {},
      styleConfig: this.$store.state.map.styleConfig || {},
      objectFields: {},
      objectsCounts: {},
      featuresInBboxCounts: {},
      mapBearing: 0,
      is3d: false,
      mapLoading: false,
      LayerController: null,
      TrackController: null,
      MatricesController: null,
      mapMode: "vizualization",
      popupSettings: {
        display: "none",
        top: 0,
        left: 0,
        values: []
      },
      popupListSettings: {
        display: "none",
        top: 0,
        left: 0,
        values: []
      },
      editorState: cloneDeep(initialEditorState),
      timemachineState: { ...initialTimemachineState },
      timemachineFilter: {},
      isUpdate: false,
      isSecondPanelActive: false,
      secondPanelMode: "layer-settings",
      layerSettingsId: "",
      deleteModal: false,
      createModal: false,
      prevSource: "",
      baseURL: constants.URL,
      downloadURL: "download_share_resource",
      token: this.$store.state.auth.token,
      directionIconPath
    };
  },
  watch: {
    activeCards() {
      this.$nextTick(() => this.mapgl.resize());
    },
    activeCard: function(val) {
      if (!val) this.$store.commit("SET_MAP_ACTIVE_CARD_FEATURE", null);
    },
    "$store.state.map.flyToGeom": function(geom) {
      if (!geom) return;
      const bounds = extent(geom);

      this.mapgl.fitBounds(bounds, {
        padding: 20
      });
      this.$store.commit("SET_FLY_TO_GEOM", null);
    },
    activeCardFeature(val) {
      toggleActiveObject(this, val);
    }
  },
  computed: {
    activeCards() {
      return this.$store.state.commonData.activeCards.map || [];
    },
    activeCard() {
      return this.$store.state.commonData.activeCard.map;
    },
    demoGuest() {
      return this.$store.state.demoGuest;
    },
    activeCardFeature() {
      return this.$store.state.map.activeCardFeature;
    }
  },
  methods: {
    saveMapParams() {
      if (this.$store.state.demoGuest) return;

      const zoom = this.mapgl.getZoom();
      const center = this.mapgl.getCenter();

      localStorage.setItem("mapBICenter", JSON.stringify(center));
      localStorage.setItem("mapBIZoom", JSON.stringify(zoom));
    },
    changeProp({ field, value }) {
      this[field] = value;
    },
    toggleModule({ type, data }) {
      const { id } = data;

      switch (type) {
        case "settings":
          if (
            this.isSecondPanelActive &&
            this.secondPanelMode === "layer-settings" &&
            (this.layerSettingsId === id || !id)
          ) {
            this.isSecondPanelActive = false;
            this.layerSettingsId = "";
          } else {
            this.secondPanelMode = "layer-settings";
            this.isSecondPanelActive = true;
            this.layerSettingsId = id;
          }
          this.$nextTick(() => this.mapgl.resize());
          break;
        case "editor":
          if (this.mapLoading) return;
          this.EditorController.toggleEditor(data);
          break;
      }
    },
    toggleMode(mode) {
      this.mapMode = mode;
    },
    toggleBaselayer({ id, name }) {
      const style =
        name === "dark"
          ? `mapbox://styles/aplussmoscow/ckea67vfv6oxu1aoxuaikbgvm`
          : `mapbox://styles/mapbox/${name}-v9`;
      this.$store.commit("SET_BASELAYER_ID", id);
      this.baselayerId = id;

      const activeLayers = [...this.$store.state.map.activeLayers];
      this.mapgl.setStyle(style);
      this.$store.commit("CLEAR_ACTIVE_LAYERS");
      this.mapgl.once("style.load", () => {
        activeLayers.forEach(id => {
          this.mapgl.off("moveend", this.LayerController.handlers[id]);
          this.LayerController.toggleLayer({ id });
        });
      });
    },
    getObjectsLimit() {
      return 10000;
    },
    // timemachine helpers
    changeTimemachineField(field) {
      const { layerId, id } = this.timemachineState;
      const filter = this.styleConfig[layerId].filters.find(f => f.id === id);
      this.LayerController.changeFilterField(filter, id, field);
      this.changeTimemachineState(filter);
      this.timemachineState.fieldChanged = true;
    },
    changeTimemachineState(value) {
      this.timemachineState.id = value.id;
      this.timemachineState.min = value.min;
      this.timemachineState.max = value.max;
      this.timemachineState.range = [...value.range];
      this.timemachineState.allCount = value.allCount;
      this.timemachineState.chunks = value.chunks;
      this.timemachineState.attribute = value.field;
      this.timemachineState.enabled = true;
      this.timemachineState.point = value.range[1];
      this.timemachineState.layerId = value.layerId;
      this.timemachineFilter = value;
      this.timemachineState.sourceName = value.sourceName;
      this.timemachineState.sourceId = value.sourceId;

      if (value.sourceId) {
        this.timemachineState.isNested = true;
      }
    },
    async closeTimemachine() {
      const { attribute, layerId } = this.timemachineState;
      this.timemachineState = { ...initialTimemachineState };
      await this.LayerController.timemachineChangeHandler({
        attribute,
        layerId
      });
    },
    timemachineChangeHandler() {
      const { attribute, layerId } = this.timemachineState;

      this.LayerController.timemachineChangeHandler({
        attribute,
        layerId
      });
    },
    // map-editor - delete objects helpers
    toggleDeleteModal(value) {
      this.deleteModal = value;
    },
    // map-editor - create object helpers
    toggleCreateModal(value) {
      this.createModal = value;
    }
  },
  async mounted() {
    initMap(this);
    initControllers(this);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.saveMapParams);
    this.saveMapParams();
    const { activeLayers } = this.$store.state.map;

    this.$store.commit("SET_INITIAL_ACTIVE_LAYERS", [...activeLayers]);
    this.$store.commit("CLEAR_ACTIVE_LAYERS");
  }
};
</script>

<style lang="scss" src="./map.scss"></style>

<i18n>
{
  "ru": {
    "map": "Карта"
  },
  "en": {
    "map": "Map"
  }
}
</i18n>
